<template>
  <div>
    <!-- Transient snackbars (bottom-right, auto-dismiss) -->
    <div
      class="snackbar-container"
    >
      <transition-group
        name="snackbar"
        tag="div"
      >
        <div
          v-for="snack in snackbarSnacks"
          :key="snack.id"
          class="snackbar"
          @mouseenter="pause(snack)"
          @mouseleave="resume(snack)"
        >
          <!-- eslint-disable vue/no-v-html -->
          <span
            class="message"
            v-html="snack.message"
          />
          <!-- eslint-enable vue/no-v-html -->

          <universal-button
            type="base"
            color="transparent"
            :label="$t('base.close')"
            style="border: unset !important; color: #869db6;"
            @click="remove(snack.id)"
          />
        </div>
      </transition-group>
    </div>

    <!--
      Blocking notifications (centered modal, dismissed only via OK).
      Shown one at a time as a queue — the next one appears after the current
      is confirmed.
    -->
    <div
      v-if="currentBlockingSnack"
      class="blocking-overlay"
    >
      <div
        :key="currentBlockingSnack.id"
        class="blocking-dialog"
      >
        <div
          v-if="currentBlockingSnack.title"
          class="blocking-title"
        >
          {{ currentBlockingSnack.title }}
        </div>
        <!-- eslint-disable vue/no-v-html -->
        <div
          class="blocking-message"
          v-html="currentBlockingSnack.message"
        />
        <!-- eslint-enable vue/no-v-html -->
        <universal-button
          type="save"
          color="#65AD45"
          :label="$t('base.ok')"
          class="blocking-confirm"
          @click="remove(currentBlockingSnack.id)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { notificationBus, NOTIFICATION_PRESENTATION } from '../../services/notification/notificationBus'
import UniversalButton from '../buttons/UniversalButton.vue'
export default {
  name: 'CustomSnackbar',

  components: {
    UniversalButton
  },

  data () {
    return {
      snacks: []
    }
  },

  computed: {
    snackbarSnacks () {
      return this.snacks.filter(snack => snack.presentation !== NOTIFICATION_PRESENTATION.BLOCKING)
    },

    blockingSnacks () {
      return this.snacks.filter(snack => snack.presentation === NOTIFICATION_PRESENTATION.BLOCKING)
    },

    // Only the first blocking notification is shown; the rest wait in the queue
    // and surface one by one as each is confirmed via OK.
    currentBlockingSnack () {
      return this.blockingSnacks[0] || null
    }
  },

  created () {
    notificationBus.$on('notify', this.add)
  },

  beforeDestroy () {
    notificationBus.$off('notify', this.add)
  },

  methods: {
    add (snack) {
      const newSnack = {
        ...snack,
        remaining: snack.timeout,
        startedAt: Date.now()
      }

      this.snacks.push(newSnack)

      // Blocking notifications never auto-dismiss — they wait for the user.
      if (newSnack.presentation !== NOTIFICATION_PRESENTATION.BLOCKING) {
        this.startTimer(newSnack)
      }
    },

    startTimer (snack) {
      snack.timer = setTimeout(() => {
        this.remove(snack.id)
      }, snack.remaining)
    },

    pause (snack) {
      clearTimeout(snack.timer)
      snack.remaining -= Date.now() - snack.startedAt
    },

    resume (snack) {
      snack.startedAt = Date.now()
      this.startTimer(snack)
    },

    remove (id) {
      const index = this.snacks.findIndex(s => s.id === id)

      if (index !== -1) {
        const snack = this.snacks[index]
        if (snack.timer) {
          clearTimeout(snack.timer)
        }

        this.snacks.splice(index, 1)
      }
    }

  }
}
</script>

<style scoped>
.snackbar-container {
  position: fixed;
  right: 1rem;
  bottom: 1rem;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.snackbar {
  min-width: 280px;
  max-width: 420px;
  background: #323232;
  color: #f6f5f5;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  box-shadow: 0 6px 16px rgba(0,0,0,.3);
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: 'Satoshi', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0;
  margin-top: 0.5rem;
}

.close {
  background: none;
  border: none;
  color: white;
  font-size: 18px;
  cursor: pointer;
}

.snackbar-enter-active,
.snackbar-leave-active {
  transition: all 0.3s ease;
}

.snackbar-enter,
.snackbar-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.message {
  /* Wrap on word boundaries; only split a single overlong token (eg. a URL)
     when it can't fit on its own line. */
  min-width: 0;
  overflow-wrap: anywhere;
}

.blocking-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  background: rgba(17, 24, 39, 0.5);
  padding: 1rem;
}

.blocking-dialog {
  width: 100%;
  max-width: 480px;
  background: #FFFFFF;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  font-family: 'Satoshi', sans-serif;
}

.blocking-title {
  font-weight: 500;
  font-size: 18px;
  line-height: 24px;
  color: #111827;
  margin-bottom: 12px;
}

.blocking-message {
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: #111827;
  word-break: break-word;
  margin-bottom: 20px;
}

.blocking-confirm {
  width: 100%;
  max-width: 200px;
  height: 40px !important;
  color: #FFFFFF !important;
  border: unset !important;
}
</style>
